if (!customElements.get('channelsight-retailers')) {
	class ChannelsightRetailers extends HTMLElement {
		static observedAttributes = ['data-barcode'];
		static vendorNames = {
			AmazonUS: 'Amazon',
			HomeDepot: 'Home Depot',
			Wayfair: 'Wayfair'
		};
		constructor() {
			super();

			this.connected = false;

			this.availabilityTypes = {
				inStock: 'InStock'
			};
			this.sortOptions = {
				price: 'price',
				title: 'title',
				pinned: 'pinned'
			};
			this.pinnedRetailerOrder = ['AmazonUS', 'Wayfair', 'HomeDepot'];
		}

		connectedCallback() {
			this.connected = true;
			if (this.getAttribute('data-disable') == 'true') {
				this.classList.add('hidden');
				return;
			}
			this.initialize();
		}

		attributeChangedCallback(name, oldValue, newValue) {
			if (!this.connected) return;
			if (oldValue !== newValue) this.initialize();
		}

		getConfigData() {
			const script = this.querySelector('script[type="application/json"][data-config]');
			if (!script) return null;

			try {
				return JSON.parse(script.textContent);
			} catch (error) {
				console.error(error);
				return null;
			}
		}

		initialize() {
			this.config = this.getConfigData();
			if (!this.config || this.config?.disableOnProduct) {
				this.classList.add('hidden');
				return;
			}

			this.retailerList = this.querySelector('.retailers-list');
			if (!this.retailerList) {
				this.classList.add('hidden');
				return;
			}

			this.barcode = this.getAttribute('data-barcode');
			if (!this.barcode) {
				this.classList.add('hidden');
				return;
			}

			this.spinner = this.querySelector('.retailers-spinner');

			this.sortBy = this.config?.sortBy ? this.sortOptions[this.config.sortBy] : null;

			this.enableGlobalPriceThresholds = this.config?.enableGlobalPriceThresholds;
			this.globalPriceThreshold = this.config?.globalPriceThreshold || 0;
			// These key names match to `RetailerName` returned by the API
			this.globalPriceThresholds = this.config?.globalRetailerThresholds || {};
			this.variantPriceThresholds = this.config?.variantRetailerThresholds || {};
			this.variantDisabledRetailers = this.config?.variantDisabledRetailers || {};
			this.variantMerchantComparisons = this.config?.variantRetailerMerchantComparisons || {};
			this.merchantPrice = this.config?.currentVariantPrice || 0;

			this.retailerProducts = [];

			this.fetchRetailers();
		}

		getCookie(name) {
			const regex = new RegExp(`(^| )${name}=([^;]+)`);
			const match = document.cookie.match(regex);
			if (match) {
				return match[2];
			}
		}

		fetchRetailers() {
			if (this.submitting) {
				this.submitting = false;
				this.controller.abort();
			}
			this.controller = new AbortController();
			this.signal = this.controller.signal;
			this.submitting = true;
			this.showSpinner();

			const medium = this.getCookie('utm_medium');
			const content = this.getCookie('utm_content');
			const source = this.getCookie('utm_source');
			const campaign = this.getCookie('utm_campaign');
			const gclid = this.getCookie('gclid');

			let endpoint = `https://api.channelsight.com/api/2.14/feed?key=c4d11394-8ab1-4b18-9f03-f9abf4a51dbf&assetId=5589&pid=${this.barcode}`;
			if (medium) endpoint += `&csi_utm_medium=${medium}`;
			if (content) endpoint += `&csi_utm_content=${content}`;
			if (source) endpoint += `&csi_utm_source=${source}`;
			if (campaign) endpoint += `&csi_utm_campaign=${campaign}`;
			if (gclid) endpoint += `&csi_subtag=${gclid}`;
			fetch(endpoint, {
				method: 'get',
				signal: this.signal
			})
				.then(function (response) {
					if (!response.ok) {
						return Promise.reject(result);
					}
					return response.json();
				})
				.then((response) => {
					if (
						!response.length ||
						!response[0].RetailerProducts ||
						!response[0].RetailerProducts.length
					) {
						this.classList.add('hidden');
						return;
					}
					this.retailerProducts = response[0].RetailerProducts;
					this.filter();
					if (!this.retailerProducts.length) {
						this.classList.add('hidden');
						return;
					}
					this.generate();
				})
				.catch((err) => {
					console.error(err);
					this.classList.add('hidden');
				})
				.finally(() => {
					this.submitting = false;
					this.hideSpinner();
				});
		}

		filter() {
			this.removeInvalidProducts();
			this.removeOutOfStock();
			this.removeDisabledRetailers();
			this.filterByVariantMerchantComparisons();
			this.filterByVariantThresholds();
			this.filterByGlobalThresholds();
			this.sortRetailers();
		}

		// Lowest price across the given products, optionally excluding one retailer
		// Returns null when there are no products to compare
		getLowestPrice(products, excluded = null) {
			return products.reduce((lowest, retailer) => {
				if (retailer === excluded) return lowest;
				const price = parseInt(retailer.Price, 10);
				return lowest === null || price < lowest ? price : lowest;
			}, null);
		}

		// Filter out $0 priced products returned (erroneously?) by the API
		removeInvalidProducts() {
			this.retailerProducts = this.retailerProducts.filter(
				(product) => product.Price !== 0 && product.RetailerName !== 'AshleyFurnitureUS'
			);
		}

		// Hide out of stock retailers
		removeOutOfStock() {
			if (!this.config?.disableOutOfStock) return;
			this.retailerProducts = this.retailerProducts.filter(
				(retailer) => retailer.Availability === this.availabilityTypes.inStock
			);
		}

		// Remove disabled retailers
		removeDisabledRetailers() {
			if (!this.variantDisabledRetailers) return;
			this.retailerProducts = this.retailerProducts.filter(
				(retailer) => this.variantDisabledRetailers[retailer.RetailerName] !== true
			);
		}

		// Compares price to the merchant price and hides if lower than the merchants listed price
		filterByVariantMerchantComparisons() {
			if (!this.variantMerchantComparisons) return;
			this.retailerProducts = this.retailerProducts.filter((retailer) => {
				const priceInDollars = retailer.Price;
				const priceInCents = Math.round(priceInDollars * 100);
				if (
					priceInCents < this.merchantPrice &&
					this.variantMerchantComparisons[retailer.RetailerName] === true
				) {
					return false;
				}
				return true;
			});
		}

		// Filter by variant-specific price thresholds
		// Runs before and takes precedence over the global filter
		// Contains both a minimum and a maximum differential value
		filterByVariantThresholds() {
			if (!this.variantPriceThresholds || this.retailerProducts.length <= 1) return;

			// Skip  when every threshold has no min/max configured
			const hasActiveThreshold = Object.values(this.variantPriceThresholds).some(
				(threshold) => threshold && (threshold.minimum < 0 || threshold.maximum > 0)
			);
			if (!hasActiveThreshold) return;

			// Negative differentials (`minimum`)
			// Removes a retailer that undercuts every competitor by more than the allowed amount
			// Run first because dropping a too-cheap outlier changes the lowest price that the positive pass compares against
			this.retailerProducts = this.retailerProducts.filter((retailer) => {
				const threshold = this.variantPriceThresholds[retailer.RetailerName];
				if (!threshold || !(threshold.minimum < 0)) return true;

				// Compare against competitors (not itself) so the cheapest retailer yields
				// a negative differential.
				const competitorPrice = this.getLowestPrice(this.retailerProducts, retailer);
				if (competitorPrice === null) return true;

				const differential = parseInt(retailer.Price, 10) - competitorPrice;
				return differential >= threshold.minimum;
			});

			// Positive differentials (`maximum`)
			// Remove a retailer priced too far above the lowest price
			if (this.retailerProducts.length <= 1) return;
			const lowestPrice = this.getLowestPrice(this.retailerProducts);
			this.retailerProducts = this.retailerProducts.filter((retailer) => {
				const threshold = this.variantPriceThresholds[retailer.RetailerName];
				if (!threshold || !(threshold.maximum > 0)) return true;

				// Zero for the cheapest retailer; positive for the rest
				const differential = parseInt(retailer.Price, 10) - lowestPrice;
				return differential <= threshold.maximum;
			});
		}

		// Filter by global price thresholds
		filterByGlobalThresholds() {
			if (!this.enableGlobalPriceThresholds || this.retailerProducts.length <= 1) return;

			const lowestPrice = this.getLowestPrice(this.retailerProducts) ?? 0;
			this.retailerProducts = this.retailerProducts.filter((retailer) => {
				const retailerThreshold = this.globalPriceThresholds[retailer.RetailerName] || 0;
				const retailerPrice = parseInt(retailer.Price, 10);

				// Keep when no filtering applies or this is the lowest priced retailer
				if (
					(retailerThreshold === 0 && this.globalPriceThreshold === 0) ||
					lowestPrice === 0 ||
					retailerPrice === lowestPrice
				) {
					return true;
				}

				// Retailers without a unique threshold fall back to the global threshold
				if (retailerThreshold === 0) {
					return (
						this.globalPriceThreshold > 0 &&
						retailerPrice - lowestPrice < this.globalPriceThreshold
					);
				}

				return retailerThreshold > 0 && retailerPrice - lowestPrice < retailerThreshold;
			});
		}

		// Sort retailer output list
		sortRetailers() {
			if (!this.sortBy || this.retailerProducts.length <= 1) return;

			this.retailerProducts.sort((a, b) => {
				if (this.sortBy === this.sortOptions.price) {
					return a.Price - b.Price;
				} else if (this.sortBy === this.sortOptions.title) {
					const firstRetailer = Array.from(a.FriendlyName)[0];
					const secondRetailer = Array.from(b.FriendlyName)[0];
					return firstRetailer.localeCompare(secondRetailer);
				} else if (this.sortBy === this.sortOptions.pinned) {
					const firstRetailerPosition = this.pinnedRetailerOrder.indexOf(a.RetailerName);
					const secondRetailerPosition = this.pinnedRetailerOrder.indexOf(b.RetailerName);
					const firstRetailerPriority =
						firstRetailerPosition !== -1
							? firstRetailerPosition
							: this.pinnedRetailerOrder.length;
					const secondRetailerPriority =
						secondRetailerPosition !== -1
							? secondRetailerPosition
							: this.pinnedRetailerOrder.length;
					return firstRetailerPriority - secondRetailerPriority;
				}
				// Fallback
				else {
					return 0;
				}
			});
		}

		generate() {
			if (!this.retailerProducts.length) {
				this.classList.add('hidden');
				return;
			}
			const container = document.createElement('ul');
			this.retailerProducts.forEach((retailer) => {
				const entry = document.createElement('li');
				const logoWrapper = document.createElement('div');
				logoWrapper.classList.add('channelsight-logo');
				const logo = document.createElement('img');
				logo.src = retailer.RetailerLogoUrl;
				logo.setAttribute('width', '70px');
				logo.setAttribute('height', '70px');
				logo.alt = retailer.RetailerName;
				logoWrapper.appendChild(logo);
				entry.appendChild(logoWrapper);
				const price = document.createElement('p');
				price.classList.add('channelsight-price');
				price.textContent = retailer.RetailerCurrencySymbol + Math.ceil(retailer.Price);
				entry.appendChild(price);
				const buttonContainer = document.createElement('div');
				buttonContainer.classList.add('channelsight-buy');
				const button = document.createElement('a');
				button.href = retailer.ClickThruUrl;
				button.target = '_blank';
				const vendorName =
					(ChannelsightRetailers.vendorNames &&
						ChannelsightRetailers.vendorNames[retailer.RetailerName]) ||
					retailer.RetailerName;
				const longName = document.createElement('span');
				longName.classList.add('small-desktop-hide', 'medium-hide', 'small-hide');
				longName.textContent = vendorName
					? `${window.variantStrings.viewOn} ${vendorName}`
					: window.variantStrings.buyNow;
				const shortName = document.createElement('span');
				shortName.classList.add('large-desktop-hide');
				shortName.textContent = window.variantStrings.buyNow;
				button.appendChild(longName);
				button.appendChild(shortName);
				buttonContainer.appendChild(button);
				entry.appendChild(buttonContainer);
				container.appendChild(entry);
			});
			this.retailerList.textContent = '';
			this.retailerList.appendChild(container);
			this.classList.remove('hidden');
			this.hideSpinner();
			setTimeout(() => {
				this.style.setProperty('--previous-height', `${this.scrollHeight}px`);
			});
		}

		hideSpinner() {
			this.spinner?.classList.add('hidden');
		}

		showSpinner() {
			this.spinner?.classList.remove('hidden');
		}
	}

	customElements.define('channelsight-retailers', ChannelsightRetailers);
}

if (!customElements.get('sofa-arrangements')) {
	class SofaArrangements extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.arrangements = this.querySelectorAll('.arrangement__tile');
			this.label = this.querySelector('[data-arrangement]');
			this.defaultVariantId = this.getAttribute('data-default-variant') || null;
			this.handleOnClick = this.onClick.bind(this);
			this.arrangements.forEach((arrangement) => {
				arrangement.addEventListener('click', this.onClick);
			});
			this.updateActive();
		}

		disconnectedCallback() {
			this.arrangements.forEach((arrangement) => {
				arrangement.removeEventListener('click', this.onClick);
			});
		}

		onClick(event) {
			event.preventDefault();
			if (event.target.classList.contains('arrangement__tile--active')) return;
			const target = event.target.href;
			const params = target.split('?');
			if (!params.length) {
				window.location.href = target;
				return;
			}

			const searchParams = new URLSearchParams(`?${params[1]}`);
			const variant = searchParams.get('variant');
			if (!variant) {
				window.location.href = target;
				return;
			}

			const variantOptions = this.closest('product-info')?.querySelector('variant-selects');
			if (!variantOptions) {
				window.location.href = target;
				return;
			}

			const variantId = parseInt(variant, 10);
			const newVariant = variantOptions.getVariantData().find((variant) => {
				return variant.id === variantId;
			});

			if (!newVariant) {
				window.location.href = target;
				return;
			}

			const fieldsets = Array.from(variantOptions.querySelectorAll('fieldset'));
			const requiredOptions = newVariant.options.length;

			if (fieldsets.length !== requiredOptions) {
				window.location.href = target;
				return;
			}

			let foundOptions = 0;

			fieldsets.forEach((fieldset, index) => {
				const option = fieldset.querySelector(`[value='${newVariant.options[index]}']`);
				if (option) {
					option.checked = true;
					foundOptions++;
				}
			});

			if (foundOptions !== requiredOptions) {
				window.location.href = target;
				return;
			}

			variantOptions.onVariantChange(event);
		}

		updateActive() {
			this.urlParams = new URLSearchParams(window.location.search);
			this.variantId = this.urlParams.get('variant');
			if (!this.variantId) {
				const variantOptions = this.closest('.product__info-container')?.querySelector(
					'variant-radios'
				);
				this.variantId = variantOptions?.currentVariant?.id;
			}
			if (!this.variantId) this.variantId = this.defaultVariantId;
			if (!this.variantId) return;
			this.currentLocation = window.location.pathname + `?variant=${this.variantId}`;
			this.activeIcon = null;
			this.arrangements.forEach((arrangement) => {
				const productLinks = arrangement.getAttribute('data-variants');
				const active = productLinks.includes(this.currentLocation);
				arrangement.classList.toggle('arrangement__tile--active', active);
				if (active) {
					this.activeIcon = arrangement;
				}
			});
			// Sofa fallback
			if (!this.activeIcon) {
				this.arrangements.forEach((arrangement) => {
					const productLinks = arrangement.getAttribute('data-variants');
					if (productLinks.includes(window.location.pathname)) {
						this.activeIcon = arrangement;
						arrangement.classList.add('arrangement__tile--active');
					}
				});
			}
			if (this.activeIcon) {
				this.label.textContent = this.activeIcon.getAttribute('data-label');
			}
		}
	}

	customElements.define('sofa-arrangements', SofaArrangements);
}

if (!customElements.get('product-delivery')) {
	class ProductDelivery extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.offsets = {
				startDate: {
					smallParcel: 2,
					LTL: 3
				},
				endDate: {
					smallParcel: 6,
					LTL: 20
				}
			};
			this.smallParcel = this.getAttribute('data-small-parcel') == 'true' ? true : false;
			this.LTL = this.getAttribute('data-ltl') == 'true' ? true : false;
			this.message = this.querySelector('[data-message]');
			if (!this.smallParcel && !this.LTL) {
				this.classList.add('hidden');
				this.parentElement.classList.add('hidden');
				return;
			}
			if (!this.message) {
				this.classList.add('hidden');
				this.parentElement.classList.add('hidden');
				return;
			}
			this.generateMessage();
		}

		generateMessage() {
			// https://stackoverflow.com/a/563442
			// Incrementing and setting an already instanced date apparently causes shenanigans with month/year rollovers.
			// Creating an incrementing function against the date object and returning a new date instance avoids this.
			Date.prototype.addDays = function (days) {
				const date = new Date(this.valueOf());
				date.setDate(date.getDate() + days);
				return date;
			};
			const startDate = new Date();
			const endDate = new Date();
			const startOffset = this.smallParcel
				? this.offsets.startDate.smallParcel
				: this.offsets.startDate.LTL;
			const endOffset = this.smallParcel
				? this.offsets.endDate.smallParcel
				: this.offsets.endDate.LTL;

			const newStartDate = startDate.addDays(startOffset);
			const newEndDate = endDate.addDays(endOffset);

			const deliveryMessage = `Free delivery by ${newStartDate.toLocaleString('default', {
				month: 'short'
			})} ${newStartDate.getDate()} - ${newEndDate.toLocaleString('default', {
				month: 'short'
			})} ${newEndDate.getDate()}`;
			this.message.textContent = deliveryMessage;
			this.classList.remove('hidden');
			this.parentElement.classList.remove('hidden');
		}
	}

	customElements.define('product-delivery', ProductDelivery);
}

if (!customElements.get('product-stock')) {
	class ProductStock extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.backInStockDate = this.getAttribute('data-back-in-stock-date');
			this.available = this.getAttribute('data-available') == 'true' ? true : false;
			this.quantity = this.getAttribute('data-quantity');
			this.message = this.querySelector('[data-message]');
			this.spinner = this.querySelector('[data-spinner]');

			if (!this.message) {
				this.classList.add('hidden');
				this.parentElement.classList.add('hidden');
				return;
			}

			// Region-specific warehouse stock, mirroring the availability gating applied to the buy button.
			this.warehouseInventory = {
				us_east_inventory: this.getAttribute('data-us-east-inventory'),
				us_west_inventory: this.getAttribute('data-us-west-inventory')
			};

			// Re-render once the geolocation service resolves after this element connects.
			this.locationChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.locationChange, () => {
				this.resolveMessage();
			});

			// Spin only while the outcome actually depends on an unresolved location.
			if (this.shouldAwaitLocation()) {
				this.showLoading();
				window.locationService.getLocation().finally(() => this.resolveMessage());
			} else {
				this.generateMessage();
			}
		}

		disconnectedCallback() {
			if (this.locationChangeUnsubscriber) this.locationChangeUnsubscriber();
		}

		// True when this product is location-gated but the visitor's location hasn't resolved yet.
		shouldAwaitLocation() {
			if (!window.locationService || window.locationService.loaded) return false;
			return [
				this.warehouseInventory.us_east_inventory,
				this.warehouseInventory.us_west_inventory
			].some((value) => value !== null && value !== '' && Number.isFinite(Number(value)));
		}

		showLoading() {
			this.classList.remove('hidden');
			this.parentElement.classList.remove('hidden');
			this.message.classList.add('hidden');
			this.spinner?.classList.remove('hidden');
		}

		resolveMessage() {
			this.spinner?.classList.add('hidden');
			this.message.classList.remove('hidden');
			this.generateMessage();
		}

		// Stock for the warehouse serving the visitor's region, or null when it can't be resolved
		// (unmapped region, missing data, or the service not yet loaded) so we fall back to defaults.
		getRegionalStock() {
			const region = window.locationService?.region;
			return window.RegionalInventory?.getStock(region, this.warehouseInventory) ?? null;
		}

		generateMessage() {
			this.classList.remove('hidden');

			const regionalStock = this.getRegionalStock();
			const available = regionalStock === null ? this.available : regionalStock > 0;
			const quantity = regionalStock === null ? this.quantity : regionalStock;

			if (quantity && available) {
				const messageTemplate = window.variantStrings.inventoryStockCount;
				const newMessage = messageTemplate.replace('{{ quantity }}', quantity);
				this.message.textContent = newMessage;
				return;
			}
			if (!this.backInStockDate) {
				this.message.textContent = window.variantStrings.stockComingSoon;
				this.classList.remove('hidden');
				this.parentElement.classList.remove('hidden');
				return;
			}
		}
	}

	customElements.define('product-stock', ProductStock);
}

if (
	!customElements.get('pdp-modal-opener') &&
	!customElements.get('pdp-swatch-opener') &&
	!customElements.get('ar-callout')
) {
	class PDPModalOpener extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.handleClick = this.onClick.bind(this);
			this.target = this.getAttribute('data-target');
			this.modal = this.target ? document.getElementById(this.target) : null;
			if (!this.modal) {
				this.classList.add('hidden');
				return;
			}
			this.addEventListener('click', this.handleClick);
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.handleClick);
		}

		onClick() {
			this.modal.show(this);
		}
	}
	customElements.define('pdp-modal-opener', PDPModalOpener);

	class ARCallout extends PDPModalOpener {
		constructor() {
			super();
		}

		connectedCallback() {
			this.handleClick = this.onClick.bind(this);
			this.target = this.getAttribute('data-target');
			this.modal = this.target ? document.getElementById(this.target) : null;
			if (!this.modal) {
				this.classList.add('hidden');
				return;
			}
			this.addEventListener('click', this.handleClick);
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.handleClick);
		}

		onClick() {
			if (typeof QRCode == 'undefined') return;
			const qrCodeContainer = document.getElementById('qrCode');
			const arLinkInput = document.getElementById('arLink');
			const arLink = arLinkInput.dataset.arLink;
			qrCodeContainer.innerHTML = '';
			let isMobile = false;
			if (
				navigator.userAgent.match(/Android/i) ||
				navigator.userAgent.match(/webOS/i) ||
				navigator.userAgent.match(/iPhone/i) ||
				navigator.userAgent.match(/iPad/i) ||
				navigator.userAgent.match(/iPod/i) ||
				navigator.userAgent.match(/BlackBerry/i) ||
				navigator.userAgent.match(/Windows Phone/i)
			) {
				isMobile = true;
			}
			if (isMobile) {
				if (arLink) {
					window.open(arLink, '_blank');
				}
				return;
			} else {
				if (arLink) {
					new QRCode(qrCodeContainer, {
						text: arLink,
						width: 100,
						height: 100,
						colorDark: '#000000',
						colorLight: '#ffffff',
						correctLevel: QRCode.CorrectLevel.H
					});
				} else {
					qrCodeContainer.innerHTML = '<p>No AR link found.</p>';
				}
			}

			this.modal.show(this);
		}
	}
	customElements.define('ar-callout', ARCallout);

	class PDPSwatchOpener extends PDPModalOpener {
		constructor() {
			super();
		}

		connectedCallback() {
			this.handleClick = this.onClick.bind(this);
			this.target = this.getAttribute('data-target');
			this.modal = this.target ? document.getElementById(this.target) : null;
			this.enabled = this.getAttribute('data-free-swatches') == 'true' ? true : false;
			this.swatchCollection = this.getAttribute('data-swatch-collection');
			if (!this.modal || !this.enabled || !this.swatchCollection) {
				this.classList.add('hidden');
				return;
			}
			this.addEventListener('click', this.handleClick);
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.handleClick);
		}

		onClick() {
			this.classList.add('loading');
			this.modal.generateSwatches(this);
		}
	}
	customElements.define('pdp-swatch-opener', PDPSwatchOpener);
}

if (!customElements.get('pdp-modal')) {
	class PDPModal extends HTMLElement {
		constructor() {
			super();
			document.body.appendChild(this);
		}

		connectedCallback() {
			this.productContainer = this.querySelector('[data-products]');
			this.closeButtons = this.querySelectorAll('[data-close-modal]');
			this.modalContainer = this.querySelector('[data-container]');
			this.visible = this.classList.contains('modal--active') ? true : false;
			this.setAttribute('role', 'dialog');
			const label = this.querySelector('.modal-heading')?.textContent;
			this.setAttribute('aria-label', label || 'Information Popup');
			if (!this.visible) {
				this.setAttribute('tabindex', -1);
			}
		}

		disconnectedCallback() {
			this.unbind();
		}

		bind() {
			this.handleClose = this.hide.bind(this);
			this.handleOutsideClick = this.onOutsideClick.bind(this);
			this.closeButtons.forEach((button) => {
				button.addEventListener('click', this.handleClose);
			});
			this.addEventListener('click', this.handleOutsideClick);
		}

		unbind() {
			this.closeButtons.forEach((button) => {
				button.removeEventListener('click', this.handleClose);
			});
			this.removeEventListener('click', this.handleOutsideClick);
		}

		show(opener) {
			if (this.visible) return;
			this.opener = opener;
			document.body.classList.add('overflow-hidden');
			this.classList.add('modal--active');
			const button = this.querySelector('button');
			trapFocus(this, button);
			this.removeAttribute('tabindex');
			this.visible = true;
			this.bind();
		}

		hide() {
			this.classList.remove('modal--active');
			document.body.classList.remove('overflow-hidden');
			this.visible = false;
			removeTrapFocus();
			this.unbind();
			this.setAttribute('tabindex', -1);
		}

		onOutsideClick(event) {
			if (!this.visible) return;
			if (
				event.target !== this.modalContainer &&
				!this.modalContainer.contains(event.target) &&
				event.target !== this.opener &&
				!this.opener?.contains(event.target)
			) {
				this.hide();
			}
		}
	}
	customElements.define('pdp-modal', PDPModal);
}

if (!customElements.get('pdp-swatch-modal')) {
	class PDPSwatchModal extends HTMLElement {
		constructor() {
			super();
			this.connected = false;
			document.body.appendChild(this);
		}

		connectedCallback() {
			this.productContainer = this.querySelector('[data-products]');
			this.closeButtons = this.querySelectorAll('[data-close-modal]');
			this.handleClose = this.hide.bind(this);
			this.handleOutsideClick = this.onOutsideClick.bind(this);
			this.modalContainer = this.querySelector('[data-container]');
			this.visible = this.classList.contains('modal--active') ? true : false;
			this.swatches = [];
			if (!this.visible) {
				this.setAttribute('tabindex', -1);
			}
		}

		disconnectedCallback() {
			this.unbind();
		}

		bind() {
			this.closeButtons.forEach((button) => {
				button.addEventListener('click', this.handleClose);
			});
			this.addEventListener('click', this.handleOutsideClick);
		}

		unbind() {
			this.closeButtons.forEach((button) => {
				button.removeEventListener('click', this.handleClose);
			});
			this.removeEventListener('click', this.handleOutsideClick);
		}

		show() {
			this.visible = true;
			this.classList.add('modal--active');
			document.body.classList.add('overflow-hidden');
			const button = this.querySelector('button');
			this.opener.classList.remove('loading');
			trapFocus(this, button);
			this.removeAttribute('tabindex');
			this.bind();
		}

		hide() {
			this.classList.remove('modal--active');
			document.body.classList.remove('overflow-hidden');
			removeTrapFocus();
			this.unbind();
			this.setAttribute('tabindex', -1);
			this.visible = false;
		}

		onOutsideClick(event) {
			if (!this.visible) return;
			if (
				event.target !== this.modalContainer &&
				!this.modalContainer.contains(event.target) &&
				event.target !== this.opener &&
				!this.opener?.contains(event.target)
			) {
				this.hide();
			}
		}

		async generateSwatches(opener) {
			if (!opener || !opener.swatchCollection) return;
			if (this.swatchCollection === opener.swatchCollection) {
				this.show();
				return;
			}
			this.opener = opener;
			this.productContainer.textContent = '';
			this.swatches = [];
			this.swatchCollection = opener.swatchCollection;
			this.productData = await this.fetchProducts();
			if (!this.productData) return;
			this.productData.forEach((product) => {
				this.swatches.push(new FreeSwatchProduct(product));
				this.productContainer.appendChild(this.swatches[this.swatches.length - 1].html);
			});
			this.show();
		}

		async fetchProducts() {
			const graphQuery = `
				{nodes(ids: ["${this.swatchCollection}"]){
					...on Collection {
						id
						products(first: 150) {
							edges {
								node {
									id
									title
									description
									variants(first: 1) {
										edges {
											node {
												id
											}
										}
									}
									images(first: 1) {
										edges {
											node {
												id
												url
												width
												height
											}
										}
									}
								}
							}
						}
					}
				}
			}`;

			const response = await fetch('/api/2023-04/graphql.json', {
				method: 'POST',
				headers: {
					'X-Shopify-Storefront-Access-Token': p80.storefrontToken,
					'Content-Type': 'application/graphql'
				},
				body: graphQuery
			});

			try {
				if (!response.ok) {
					const json = await response.json();
					throw new Error(json);
				}
				const json = await response.json();
				if (!json.data || !json.data.nodes || !json.data.nodes.length) {
					throw new Error('No data returned.');
				}
				return json.data.nodes[0].products.edges;
			} catch (error) {
				console.error(error);
				return null;
			}
		}
	}
	customElements.define('pdp-swatch-modal', PDPSwatchModal);
}

if (!window.FreeSwatchProduct) {
	class FreeSwatchProduct {
		constructor(data) {
			this.data = data ? data.node : null;
			if (!this.data) return;
			this.loading = false;
			this.added = false;
			this.generateHTML();
			this.bind();
		}

		generateHTML() {
			this.html = document.createElement('li');
			if (
				this.data.images &&
				this.data.images.edges &&
				this.data.images.edges[0] &&
				this.data.images.edges[0].node
			) {
				const swatchImage = document.createElement('img');
				swatchImage.setAttribute('src', this.data.images.edges[0].node.url + '&width=545');
				swatchImage.setAttribute('width', 545);
				swatchImage.setAttribute('height', 545);
				swatchImage.setAttribute('loading', 'lazy');
				swatchImage.setAttribute('alt', this.data.title);
				this.html.appendChild(swatchImage);
			}
			const swatchTitle = document.createElement('h3');
			swatchTitle.textContent = this.data.title;
			this.html.appendChild(swatchTitle);

			const swatchDescription = document.createElement('p');
			swatchDescription.textContent = this.data.description;
			this.html.appendChild(swatchDescription);

			const errorText = document.createElement('p');
			errorText.classList.add('error', 'hidden', 'small', 'brick');
			this.html.appendChild(errorText);
			this.errorText = this.html.querySelector('.error');

			const addToCartButton = document.createElement('button');
			const textWrapper = document.createElement('span');
			textWrapper.textContent = window.variantStrings.add;
			textWrapper.classList.add('add');
			addToCartButton.appendChild(textWrapper);
			const spinnerWrapper = document.createElement('span');
			spinnerWrapper.innerHTML = `
	  <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
					<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
				  </svg>
		`;
			spinnerWrapper.classList.add('spinner');
			addToCartButton.appendChild(spinnerWrapper);
			const checkWrapper = document.createElement('span');
			checkWrapper.innerHTML = `
	  <svg class="icon icon-checkmark color-foreground-text" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 9" fill="none">
		<path fill-rule="evenodd" clip-rule="evenodd" d="M11.35.643a.5.5 0 01.006.707l-6.77 6.886a.5.5 0 01-.719-.006L.638 4.845a.5.5 0 11.724-.69l2.872 3.011 6.41-6.517a.5.5 0 01.707-.006h-.001z" fill="currentColor"></path>
	  </svg>
	  `;
			checkWrapper.classList.add('check');
			addToCartButton.appendChild(checkWrapper);
			addToCartButton.setAttribute('type', 'button');
			addToCartButton.classList.add(
				'custom-button',
				'custom-button--small',
				'custom-button--white'
			);
			this.html.appendChild(addToCartButton);
			this.button = this.html.querySelector('button');
		}

		bind() {
			const swatch = this;
			if (!this.button) return;
			this.button.addEventListener('click', function () {
				swatch.addToCart();
			});
		}

		addToCart() {
			if (this.loading || this.added || !this.button) return;
			this.loading = !this.loading;
			this.button.classList.add('loading');
			this.errorText.classList.add('hidden');

			const variantData = {
				id: parseInt(
					this.data.variants.edges[0].node.id.split('/')[
						this.data.variants.edges[0].node.id.split('/').length - 1
					],
					10
				),
				quantity: 1
			};

			const formData = {
				items: [variantData]
			};

			fetch(window.Shopify.routes.root + 'cart/add.js', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(formData)
			})
				.then((response) => {
					return response.json();
				})
				.then((response) => {
					if (response.status) {
						throw new Error(response.description);
						return;
					}
					this.button.querySelector('span').textContent = window.variantStrings.added;
					this.added = true;
					this.button.classList.add('custom-button--disabled');
					if (p80 && p80.ajaxCart) {
						p80.cart.fetchCart();
					}
				})
				.catch((error) => {
					console.error('Error:', error);
					const message = error.message.replace('Error: ', '');
					this.errorText.textContent = message;
					this.errorText.classList.remove('hidden');
				})
				.finally(() => {
					this.loading = !this.loading;
					this.button.classList.remove('loading');
				});
		}
	}
	window.FreeSwatchProduct = FreeSwatchProduct;
}

if (!customElements.get('product-tabs')) {
	class ProductTabs extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.root = this.closest('.main-product-container');
			this.header = this.root?.querySelector('.product__header-sticky');
			this.stickyContainer = this.querySelector('.product__tabs-sticky');

			this.tabButtons = [...this.querySelectorAll('.product-tabs__header [data-target]')];
			this.tabs = [...this.querySelectorAll('[data-tab-id]')];
			this.tabs.unshift(this.root?.querySelector('.product'));

			if (!this.tabButtons.length || !this.tabs.length) {
				this.classList.add('hidden');
				return;
			}

			this.activeTab = this.tabButtons[0];
			this.prevActiveTab = this.activeTab;
			this.direction = 'up';
			this.prevPosition = 0;
			this.rootMargin = `0px`;

			const tasks = [this.bind.bind(this), this.setObservers.bind(this)];
			this.yieldTasks(tasks);
		}

		disconnectedCallback() {
			this.unbind();
		}

		yieldToMain() {
			return new Promise((resolve) => {
				setTimeout(resolve, 0);
			});
		}

		async yieldTasks(tasks) {
			while (tasks.length > 0) {
				const task = tasks.shift();
				task();
				await this.yieldToMain();
			}
		}

		bind() {
			this.intersectionHandler = this.onIntersect.bind(this);
			this.stickyBarUnsubscriber = subscribe(PUB_SUB_EVENTS.stickyBarUpdate, (event) => {
				this.rebound = true;
				const tasks = [this.setObservers.bind(this)];
				this.yieldTasks(tasks);
			});
			this.handleScroll = this.scrollToTab.bind(this);
			this.tabButtons.forEach((button) => {
				// Hide buttons which do not have applicable content returned
				const target = button.getAttribute('data-target');
				const targetElement = this.querySelector(`[data-tab-id='${target}']`);
				if (targetElement || target === 'overview') {
					button.addEventListener('click', this.handleScroll);
				} else {
					button.classList.add('hidden');
				}
			});
		}

		unbind() {
			if (this.stickyBarUnsubscriber) this.stickyBarUnsubscriber();
			this.tabButtons.forEach((button) => {
				button.removeEventListener('click', this.handleScroll);
			});
			this.intersectionObserver?.disconnect();
		}

		setObservers() {
			this.direction = 'up';
			this.prevPosition = window.scrollY;

			this.headerOffset = this.header.currentHeight + this.header.ignoreHeight;
			this.stickyOffset = this.stickyContainer.ignoreHeight;
			this.rootMargin = `${(this.headerOffset + this.stickyOffset) * -1}px 0px 0px 0px`;
			this.intersectionObserver?.disconnect();
			this.intersectionObserver = new IntersectionObserver(
				(entries) => this.intersectionHandler(entries),
				{threshold: 0, rootMargin: this.rootMargin}
			);
			this.tabs.forEach((tab) => {
				this.intersectionObserver.observe(tab);
			});
		}

		onIntersect(entries) {
			entries.forEach((entry) => {
				this.direction = window.scrollY > this.prevPosition ? 'down' : 'up';
				this.prevPosition = window.scrollY;
				if (!this.shouldUpdate(entry)) return;
				const target = this.getTargetSection(entry);
				this.setActive(target);
			});
			this.rebound = false;
		}

		shouldUpdate(entry) {
			if (this.direction === 'down' && !entry.isIntersecting) {
				return true;
			}
			if (this.direction === 'up' && entry.isIntersecting && !this.rebound) {
				return true;
			}
			return false;
		}

		getTargetSection(entry) {
			if (this.direction === 'up') return entry.target;
			const index = this.tabs.findIndex((tab) => tab === entry.target);
			if (index >= this.tabs.length - 1) {
				return this.tabs[this.tabs.length - 1];
			} else {
				return this.tabs[index + 1];
			}
		}

		setActive(tab) {
			if (!tab) return;
			if (this.activeTab) this.prevActiveTab = this.activeTab;
			const type = tab.getAttribute('data-tab-id') || 'overview';
			this.activeTab?.classList.remove('product-tabs__button--active');
			this.activeTab = null;
			this.tabButtons.forEach((button) => {
				const buttonType = button.getAttribute('data-target');
				if (buttonType === type) {
					this.activeTab = button;
					this.activeTab?.classList.add('product-tabs__button--active');
					this.updateActiveButton();
				}
			});
			if (!this.activeTab) {
				this.prevActiveTab.classList.add('product-tabs__button--active');
			} else if (this.activeTab && this.activeTab !== this.prevActiveTab) {
				this.prevActiveTab.classList.remove('product-tabs__button--active');
			}
		}

		updateActiveButton() {
			if (!this.activeTab) return;
			const container = this.activeTab.closest('ul');
			const containerBounding = container.getBoundingClientRect();
			const containerWidth = containerBounding.width;
			const containerOffset = container.scrollLeft;

			const buttonBounding = this.activeTab.getBoundingClientRect();
			const buttonWidth = buttonBounding.width;
			const buttonOffset = this.activeTab.offsetLeft;

			const lastVisible = containerWidth + containerOffset;

			const isVisible =
				buttonOffset + buttonWidth <= lastVisible && buttonOffset >= containerOffset;

			if (isVisible) {
				this.style.setProperty('--highlight-width', `${buttonWidth}px`);
				this.style.setProperty('--highlight-offset', `${buttonOffset - containerOffset}px`);
				return;
			}

			const scrollOffset =
				buttonBounding.right > containerBounding.right
					? containerOffset + buttonBounding.right - containerBounding.right
					: buttonOffset;

			this.style.setProperty('--highlight-width', `${buttonWidth}px`);
			this.style.setProperty('--highlight-offset', `${buttonOffset - scrollOffset}px`);

			container.scrollTo({
				top: 0,
				left: scrollOffset,
				behavior: 'smooth'
			});
		}

		scrollToTab(event) {
			if (!event) return;
			const target = event.target.getAttribute('data-target');
			if (target === 'overview') {
				window.scroll({
					top: 0,
					behavior: 'smooth'
				});
				return;
			}

			const targetElement = this.querySelector(`[data-tab-id='${target}']`);
			if (!targetElement) return;
			const bounding = targetElement.getBoundingClientRect();
			const offset = bounding.top + window.scrollY;
			let finalOffset;
			if (target === 'overview') {
				finalOffset = offset;
			} else {
				finalOffset = offset - this.headerOffset + 1;
			}
			window.scroll({
				top: finalOffset,
				behavior: 'smooth'
			});
		}
	}
	customElements.define('product-tabs', ProductTabs);
}

if (!customElements.get('product-coupon')) {
	class ProductCoupon extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.salePrice = this.parentElement.querySelector(
				'.price--on-sale .price__sale .price-item--regular'
			);
			if (!this.salePrice) return;
			this.determineOffset();
			this.resizeObserver = new ResizeObserver(() => this.determineOffset());
			this.resizeObserver.observe(this.salePrice);
		}

		disconnectedCallback() {
			this.resizeObserver?.disconnect();
		}

		determineOffset() {
			const salePriceWidth = this.salePrice.getBoundingClientRect().width;
			const offset = salePriceWidth + 6;
			this.setAttribute('style', `--offset: ${offset}px`);
		}
	}
	customElements.define('product-coupon', ProductCoupon);
}
